/**
 * @param {NodeListOf<Element>} elements
 */
App.prototype.comments = function (elements = null) {

    /**
     * @type {NodeListOf<Element>}
     */
    const comments = elements || document.querySelectorAll('[data-comments]');

    if (!comments.length) return;

    /**
     * @typedef {Object} User
     * @property {string} unique_id - The unique identifier for the user.
     * @property {string} name - The name of the user.
     * @property {boolean} has_purchased - Whether the user has purchased the product.
     */

    /**
     * @typedef {Object} Options
     * @property {string} model - The model name.
     * @property {string|number} id - The identifier, can be a string or number.
     * @property {boolean} is_commentable - Indicates if comments are allowed.
     * @property {boolean} is_comment_rating_enabled - Indicates if comment ratings allowed.
     * @property {boolean} is_comment_email_enabled - Indicates if comment email is allowed.
     * @property {boolean} is_comment_phone_enabled - Indicates if comment phone is allowed.
     * @property {boolean} is_comment_images_enabled - Indicates if comment images are allowed.
     * @property {boolean} is_comment_only_registered - Indicates if only registered users can comment.
     * @property {boolean} is_comment_only_purchased - Indicates if only registered and purchased users can comment.
     * @property {string} csrf_token - The CSRF token for security.
     * @property {null|User} user - The user object or null if not logged in.
     * @property {Routes} routes - The routes object.
     */

    for (const comment of comments) {

        /** @type {Options} */
        const options = JSON.parse(comment.dataset.comments);

        if (options.is_commentable) {
            // Check if user can comment based on registration/purchase requirements
            const canComment = checkCommentPermission(options);

            if (canComment === true) {
                buildForm(comment, options);
                handleFormSubmit(comment.querySelector('form'), options);
                handleImages(comment.querySelector('form'));
            } else {
                // Show appropriate message BEFORE showing the form
                showCommentRestriction(comment, canComment, options);
            }
        }else{
            commentsDisabled(comment, options);
        }

        renderComments(comment, 1, options);
    }

    /**
     * Check if user has permission to comment
     * @param {Options} options
     * @return {boolean|string} true if allowed, error message string if not
     */
    function checkCommentPermission(options) {
        // If only purchased users can comment (strongest restriction)
        if (options.is_comment_only_purchased) {
            if (!options.user) {
                // Guest users can comment only if is_comment_only_registered is false
                if (options.is_comment_only_registered) {
                    return 'login_required_purchased';
                }
                // Guest users allowed but must provide phone
                return true;
            }
            if (!options.user.has_purchased) {
                return 'purchase_required';
            }
        }
        // If only registered users can comment
        else if (options.is_comment_only_registered) {
            if (!options.user) {
                return 'login_required';
            }
        }

        return true;
    }

    /**
     * Show comment restriction message
     * @param {Element} el
     * @param {string} restrictionType
     * @param {Options} options
     */
    function showCommentRestriction(el, restrictionType, options) {
        let icon = '';
        let title = '';
        let message = '';
        let actionButton = '';

        switch (restrictionType) {
            case 'login_required':
                icon = `<svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                              <circle cx="12" cy="12" r="11" fill="#e3f2fd" stroke="#2196f3" stroke-width="1" />
                              <path d="M12 7v5m0 4h.01" stroke="#1565c0" stroke-width="2" stroke-linecap="round"/>
                              <circle cx="12" cy="16" r="0.5" fill="#1565c0"/>
                            </svg>`;
                title = 'Üye Girişi Gerekli';
                message = 'Yorum yapabilmek için lütfen giriş yapın veya yeni hesap oluşturun.';
                actionButton = `
                        <div class="restriction-actions">
                            <a href="${route('consumer.auth.login')}" class="btn btn-primary">
                                Giriş Yap
                            </a>
                            <a href="${route('consumer.auth.register')}" class="btn btn-outline-primary">
                                Kayıt Ol
                            </a>
                        </div>`;
                break;

            case 'login_required_purchased':
                icon = `<svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                              <circle cx="12" cy="12" r="11" fill="#fff3cd" stroke="#ffc107" stroke-width="1" />
                              <path d="M9 12l2 2 4-4m-3-3a8 8 0 100 16 8 8 0 000-16z" stroke="#856404" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>`;
                title = 'Giriş Yapın ve Ürünü Satın Alın';
                message = 'Yorum yapabilmek için giriş yapmalı ve bu ürünü satın almış olmanız gerekmektedir.';
                actionButton = `
                        <div class="restriction-actions">
                            <a href="${route('consumer.auth.login')}" class="btn btn-primary">
                                <i class="feather icon-log-in"></i> Giriş Yap
                            </a>
                            <a href="${route('consumer.auth.register')}" class="btn btn-outline-primary">
                                <i class="feather icon-user-plus"></i> Kayıt Ol
                            </a>
                        </div>`;
                break;

            case 'purchase_required':
                icon = `<svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                              <circle cx="12" cy="12" r="11" fill="#fff3e0" stroke="#ff9800" stroke-width="1" />
                              <path d="M7 9l5 5 5-5M7 15h10" stroke="#e65100" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                              <rect x="9" y="10" width="6" height="6" rx="1" stroke="#e65100" stroke-width="1.5" fill="none"/>
                            </svg>`;
                title = 'Satın Alma Gerekli';
                message = 'Yorum yapabilmek için öncelikle bu ürünü satın almanız gerekmektedir. Sadece ürünü deneyimlemiş müşterilerimiz yorum yapabilir.';
                actionButton = '';
                break;
        }

        const html = `
                <div class="comment-restriction-card" role="alert" aria-live="polite">
                  <div class="icon-wrap">
                    ${icon}
                  </div>
                  <div class="content">
                    <h4>${title}</h4>
                    <p>${message}</p>
                    ${actionButton ? `<div class="mt-3">${actionButton}</div>` : ''}
                  </div>
                </div>`;

        el.innerHTML = `<div class="comment-form-wrap"><h3>Yorum Yap</h3>${html}</div>`;
    }

    /**
     *
     * @param el {Element}
     * @param options {Options}
     */
    function commentsDisabled(el, options) {
        // Build success card
        const html = `
                            <div class="comment-success-card muted" role="status" aria-live="polite">
                              <div class="icon-wrap">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                                  <circle cx="12" cy="12" r="11" fill="#f5f6f7" stroke="#ececec" stroke-width="1" />
                                  <rect x="7" y="11.2" width="10" height="1.6" rx="0.8" fill="#9aa0a6" />
                                </svg>
                              </div>
                              <div class="content">
                                <h4>Yorumlar Kapalı!</h4>
                                <p></p>
                              </div>
                            </div>`;

        el.innerHTML = `<div class="comment-form-wrap"><h3>Yorum Yap</h3>${html}</div>`;
    }

    /**
     *
     * @param el {Element}
     * @param options {Options}
     */
    function buildForm(el, options) {

        let form = `<div class="comment-form-wrap"><h3>Yorum Yap</h3>`;
        form += `<form action="${route('consumer.api.comment.store')}" method="POST" enctype="multipart/form-data">`;

        if (options.user){
            form += `<div class="comment-logged-in">
                        ${options.user.name} olarak yorum yapıyorsunuz. <a href="${route('consumer.auth.logout')}">Çıkış yap.</a>
                    </div>`;
        }else{

            if (options.is_comment_rating_enabled){
                // Rating UI: 5-star clickable links, and a hidden input "rating"
                form += `<div class="comment-rating-wrapper">
                        <label><span>Puan</span></label>
                        <div class="rating-form">
                            <div class="rating-stars">`;
                for (let i = 1; i <= 5; i++) {
                    // SVG star (use CSS to toggle .active for filled state)
                    form += `<a href="#" class="rating-star active" data-rating="${i}" title="${i}" aria-label="${i} star">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true">
                              <path class="star-shape" d="M12 .587l3.668 7.431 8.2 1.192-5.934 5.788 1.402 8.173L12 18.896l-7.336 3.975 1.402-8.173L.132 9.21l8.2-1.192L12 .587z" stroke="currentColor" stroke-width="0.5" />
                            </svg>
                          </a>`;
                }
                form += `    </div>
                            <select name="rating" class="d-none" aria-hidden="true">
                                <option value=""></option>
                                <option value="1">1</option>
                                <option value="2">2</option>
                                <option value="3">3</option>
                                <option value="4">4</option>
                                <option value="5" selected>5</option>
                            </select>
                        </div>
                    </div>`;
            }

            const commentEmail = options.is_comment_email_enabled ? `<label>
                            <span>E-Posta</span>
                            <input type="email" name="email">
                        </label>` : '';

            const phoneRequired = options.is_comment_only_purchased ? ' required' : '';
            const commentPhone = options.is_comment_phone_enabled ? `<label>
                            <span>Telefon${options.is_comment_only_purchased ? ' <span style="color: red;">*</span>' : ''}</span>
                            <input type="tel" name="phone"${phoneRequired}>
                        </label>` : '';

            form += `<div class="comment-user-form-inputs">
                        <label>
                            <span>Ad Soyad</span>
                            <input type="text" name="name" required>
                        </label>
                        ${commentEmail}
                        ${commentPhone}
                    </div>`;
        }

        const commentImages = options.is_comment_images_enabled ? `
                        <label>
                            <span>Görsel</span>
                            <input type="file" name="images[]" multiple accept="image/*">
                        </label>

                        <ul></ul>` : '';

        form += `<div class="comment-form-inputs">
                        <label>
                            <span>Yorum</span>
                            <textarea name="comment" rows="2" required></textarea>
                        </label>

                        ${commentImages}
                    </div>

                    <input type="hidden" name="harry">
                    <button type="submit" class="btn btn-default">Gönder</button>`;

        form += `</form></div>`;

        el.innerHTML = form;

        // Attach rating click handlers for the newly created markup
        const ratingWrapper = el.querySelector('.rating-form .rating-stars');
        const ratingSelect = el.querySelector('select[name="rating"]');

        if (ratingWrapper) {
            ratingWrapper.addEventListener('click', function (e) {
                const target = e.target.closest('a');
                if (!target) return;
                e.preventDefault();

                // mark active
                const anchors = Array.from(ratingWrapper.querySelectorAll('.rating-star'));
                const selected = parseInt(target.dataset.rating, 10) || 0;
                anchors.forEach(a => {
                    const r = parseInt(a.dataset.rating, 10) || 0;
                    if (r <= selected) {
                        a.classList.add('active');
                    } else {
                        a.classList.remove('active');
                    }
                });

                // update select value
                const val = target.dataset.rating;
                if (ratingSelect) {
                    ratingSelect.value = val;
                }
            });
        }
    }

    function handleFormSubmit(el, options) {
        el.addEventListener('submit', function (e) {
            e.preventDefault();

            const formData = new FormData(this);

            formData.append('commentable_type', options.model);
            formData.append('commentable_id', options.id);

            fetch(route('consumer.api.comment.store'), {
                method: 'POST',
                body: formData,
                headers: {
                    'X-CSRF-TOKEN': options.csrf_token
                }
            })
                .then(response => response.json())
                .then(data => {
                    if (data.error){
                        App.prototype.notificationError(data.error.replace(' (and 1 more error)', ''));
                        return;
                    }

                    if (data.success) {
                        // show notification (existing behavior)
                        App.prototype.notificationSuccess(data.message);

                        // Replace the form with an appealing success card
                        const container = el.closest('[data-comments]') || el.parentElement;

                        // Build success card
                        const successHTML = `
                            <div class="comment-success-card" role="status" aria-live="polite">
                              <div class="icon-wrap">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                                  <circle cx="12" cy="12" r="11" stroke="#e6f8ea" stroke-width="2" fill="#e6f8ea" />
                                  <path d="M7 12.5l2.5 2.5L17 8" stroke="#28a745" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                              </div>
                              <div class="content">
                                <h4>Teşekkür ederiz!</h4>
                                <p>${data.message || 'Başarıyla gönderildi'}</p>
                              </div>
                            </div>`;

                        // Replace the form element with success card
                        try {
                            // If container is the [data-comments] wrapper, replace the form inside it
                            if (container && container.querySelector) {
                                const formParent = el.closest('form') ? el.closest('form').parentElement : el;
                                // Insert and animate
                                formParent.innerHTML = successHTML;
                            }
                        } catch (err) {
                            // fallback: just reset form
                            el.reset();
                        }

                        return;
                    }

                    // default: show error message if present
                    App.prototype.notificationError(data.message || 'Bir hata oluştu. Lütfen tekrar deneyin.');
                })
                .catch(error => {
                    App.prototype.notificationError(error.error || error.message || 'Bir hata oluştu. Lütfen tekrar deneyin.')
                });

        });
    }

    function handleImages(el){
        const imageInput = el.querySelector('input[name="images[]"]');
        const imagePreviewList = el.querySelector('.comment-form-inputs > ul');
        const fileStore = new DataTransfer();

        if (imageInput){
            imageInput.addEventListener('change', (event) => {
                for (const file of event.target.files) {
                    fileStore.items.add(file);
                }

                event.target.files = fileStore.files;
                renderPreviews();
            });

            imagePreviewList.addEventListener('click', (event) => {
                if (event.target.tagName.toLowerCase() !== 'button') return;

                const index = parseInt(event.target.dataset.index, 10);
                const currentFiles = Array.from(fileStore.files);
                currentFiles.splice(index, 1);

                fileStore.items.clear();
                currentFiles.forEach(file => fileStore.items.add(file));

                imageInput.files = fileStore.files;
                renderPreviews();
            });
        }

        function renderPreviews() {
            imagePreviewList.innerHTML = '';
            for (let i = 0; i < fileStore.files.length; i++) {
                const file = fileStore.files[i];
                const reader = new FileReader();
                reader.onload = (e) => {
                    const li = document.createElement('li');
                    li.innerHTML = `<img src="${e.target.result}" alt="${file.name}"><button type="button" data-index="${i}">X</button>`;
                    imagePreviewList.appendChild(li);
                };
                reader.readAsDataURL(file);
            }
        }

    }

    function renderComments(el, page, options) {
        // ensure we have a container area to render comments (so buildForm won't overwrite it)
        let listContainer = el.querySelector('.comments-list-wrapper');
        let listContainerInner = el.querySelector('.comments-list-wrapper-inner');
        if (!listContainer) {
            listContainer = document.createElement('div');
            listContainer.className = 'comments-list-wrapper';
            listContainer.innerHTML = `<h3>Yorumlar</h3>`;
            el.appendChild(listContainer);
        }
        if (!listContainerInner) {
            listContainerInner = document.createElement('div');
            listContainerInner.className = 'comments-list-wrapper-inner';
            listContainer.appendChild(listContainerInner);
        }

        // show loading state
        listContainerInner.innerHTML = `<div class="comments-loading">Yükleniyor...</div>`;

        fetchComments(page, options).then(payload => {
            if (!payload || !payload.success) {
                listContainerInner.innerHTML = `<div class="comments-error">Yorumlar yüklenemedi.</div>`;
                return;
            }

            const items = payload.data ? payload.data.items : [];
            const meta = payload.data ? payload.data.meta : { current_page: 1, per_page: items.length, total: items.length, last_page: 1};

            // build HTML
            const ul = document.createElement('ul');
            ul.className = 'comments-list';

            if (items.length === 0) {
                ul.innerHTML = `<li class="no-comments">Henüz yorum yok. İlk yorumu siz yazın!</li>`;
            } else {
                for (const c of items) {
                    const li = document.createElement('li');
                    li.className = 'comment-item';

                    // stars
                    const stars = document.createElement('div');
                    if (c.rating){
                        stars.className = 'comment-stars';
                        const rating = Number(c.rating) || 0;
                        for (let i = 1; i <= 5; i++) {
                            const star = document.createElement('span');
                            star.className = 'comment-star' + (i <= rating ? ' active' : '');
                            // use inline SVG star
                            star.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                            <path class="star-shape" d="M12 .587l3.668 7.431 8.2 1.192-5.934 5.788 1.402 8.173L12 18.896l-7.336 3.975 1.402-8.173L.132 9.21l8.2-1.192L12 .587z" stroke="currentColor" stroke-width="0.5"/></svg>`;
                            stars.appendChild(star);
                        }
                    }

                    const header = document.createElement('div');
                    header.className = 'comment-header';
                    const name = document.createElement('span');
                    name.className = 'comment-name';
                    name.textContent = c.name || 'Anonim';
                    const date = document.createElement('small');
                    date.className = 'comment-date';
                    date.textContent = c.created_at || '';
                    header.appendChild(name);
                    header.appendChild(date);

                    const body = document.createElement('div');
                    body.className = 'comment-body';
                    body.textContent = c.comment || '';

                    li.appendChild(stars);
                    li.appendChild(header);
                    li.appendChild(body);

                    // images
                    if (c.images && c.images.length) {
                        const imgs = document.createElement('ul');
                        imgs.className = 'comment-images';
                        for (const img of c.images) {
                            const il = document.createElement('li');
                            il.innerHTML = `<a href="${img.url}" data-fancybox="comments"><img src="${img.thumb ? img.thumb : img.url}" alt="comment image" /></a>`;
                            imgs.appendChild(il);
                        }
                        li.appendChild(imgs);
                    }

                    ul.appendChild(li);
                }
            }

            // pagination
            const pagination = document.createElement('nav');
            pagination.className = 'comments-pagination';
            const pager = document.createElement('ul');
            pager.className = 'pager-list';

            // pages: show compact window
            const last = meta.last_page || 1;
            const current = meta.current_page || 1;
            const windowSize = 5;
            let start = Math.max(1, current - Math.floor(windowSize/2));
            let end = Math.min(last, start + windowSize - 1);
            if (end - start + 1 < windowSize) start = Math.max(1, end - windowSize + 1);

            if (meta.last_page > 1){
                // previous
                const prevLi = document.createElement('li');
                prevLi.className = 'pager-prev' + (meta.current_page <= 1 ? ' disabled' : '');
                prevLi.innerHTML = `<a href="#" data-page="${Math.max(1, meta.current_page - 1)}" aria-label="previous">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                      <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </a>`;
                pager.appendChild(prevLi);

                for (let p = start; p <= end; p++) {
                    const pli = document.createElement('li');
                    pli.className = 'pager-page' + (p === current ? ' active' : '');
                    pli.innerHTML = `<a href="#" data-page="${p}">${p}</a>`;
                    pager.appendChild(pli);
                }

                // next
                const nextLi = document.createElement('li');
                nextLi.className = 'pager-next' + (current >= last ? ' disabled' : '');
                nextLi.innerHTML = `<a href="#" data-page="${Math.min(last, current + 1)}" aria-label="next">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                      <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </a>`;
                pager.appendChild(nextLi);

                pagination.appendChild(pager);
            }

            // assemble container
            listContainerInner.innerHTML = '';
            listContainerInner.appendChild(ul);
            listContainerInner.appendChild(pagination);

            // attach pagination click handling (event delegation)
            pagination.addEventListener('click', (ev) => {
                const a = ev.target.closest('a[data-page]');
                if (!a) return;
                ev.preventDefault();
                const toPage = parseInt(a.dataset.page, 10) || 1;
                if (toPage === current) return;
                // show small loading state inside listContainer
                if (listContainerInner.querySelector('.comments-list')){
                    listContainerInner.querySelector('.comments-list').classList.add('comments-loading-state');
                }
                renderComments(el, toPage, options);
            });

        }).catch(() => {
            listContainerInner.innerHTML = `<div class="comments-error">Yorumlar yüklenemedi.</div>`;
        });
    }

    function fetchComments(page = 1, options) {
        const formData = new FormData();

        formData.append('commentable_type', options.model);
        formData.append('commentable_id', options.id);

        return new Promise((resolve, reject) => {
            fetch(route('consumer.api.comments', {page}), {
                method: 'POST',
                body: formData,
                headers: {
                    'X-CSRF-TOKEN': options.csrf_token,
                }
            })
            .then(response => {
                resolve(response.json());
            })
            .catch(error => {
                reject(error);
            })
        })
    }

}

App.prototype.comments();
